import React, { useState, useMemo, useCallback, useRef } from 'react'
import { useRouteMatch } from 'react-router-dom'
import {
  IonSegment,
  IonSegmentButton,
  IonLabel,
  IonIcon,
  IonButton,
  IonToggle,
  IonItem,
  isPlatform,
} from '@ionic/react'
import { mapOutline, listOutline, searchOutline } from 'ionicons/icons'

import AppLayout from 'ui/layouts/AppLayout'

import GoogleMaps, {
  ExtendedMarkerOptions,
} from 'ui/components/maps/GoogleMaps'
import AssetCardList from 'ui/components/lists/AssetCardList'
import LoadingOverlay from 'ui/components/loaders/LoadingOverlay'

import { useCurrentLocale } from 'context/LocaleContext'
import useTranslations from 'hooks/useTranslation'
import useModel from 'hooks/useModel'
import { useAssetView } from 'hooks/assets'
import { useDebounce } from 'hooks/useDebounce'

import { Bounds } from 'core/Geolocation'
import { getPluralTitle, getTitleField, getColor } from 'core/ContentModel'

import './ImportAssetByLocation.css'

enum Tabs {
  Map = 'map',
  List = 'list',
}

const ImportAssetByLocation: React.FC = () => {
  const t = useTranslations()
  const locale = useCurrentLocale()

  // model
  const { params } = useRouteMatch<{ modelId: string }>()
  const { modelId } = params

  const { data: model } = useModel(modelId)
  const titleField = getTitleField(model)
  const modelTitle = model ? getPluralTitle(locale, model) : ''
  const color = model ? getColor(model) : undefined

  // tabs
  const [selectedTab, setSelectedTab] = useState<Tabs>(Tabs.Map)
  const handleTabChange = (e: CustomEvent) => setSelectedTab(e.detail.value)

  // Search mode control:
  // - autoSearch: if true, searches automatically when map moves
  // - if false, user must click "Search" button
  const [autoSearch, setAutoSearch] = useState<boolean>(false)

  // Bounds management
  const [searchBounds, setSearchBounds] = useState<Bounds>() // bounds used for query
  const currentBoundsRef = useRef<Bounds>() // current map bounds (always updated)

  const debouncedBounds = useDebounce<Bounds | undefined>(searchBounds, 600)

  // Handle bounds update from map
  const handleBoundsUpdated = useCallback(
    (newBounds: Bounds) => {
      currentBoundsRef.current = newBounds
      if (autoSearch) {
        setSearchBounds(newBounds)
      }
    },
    [autoSearch],
  )

  // Manual search: use current map bounds
  const handleManualSearch = useCallback(() => {
    if (currentBoundsRef.current) {
      setSearchBounds(currentBoundsRef.current)
    }
  }, [])

  // Toggle auto search
  const handleAutoSearchToggle = useCallback((checked: boolean) => {
    setAutoSearch(checked)
    // If enabling auto search, trigger search with current bounds
    if (checked && currentBoundsRef.current) {
      setSearchBounds(currentBoundsRef.current)
    }
  }, [])

  // assets query
  const { data, isLoading } = useAssetView(
    modelId,
    { bounds: debouncedBounds },
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      enabled: Boolean(debouncedBounds),
    },
  )

  const assets = useMemo(() => {
    return (
      data?.map((asset) => ({
        id: asset.id,
        title: titleField ? asset.data[titleField] : '-',
        modifiedAt: new Date(asset.updatedAt),
      })) ?? []
    )
  }, [data, titleField])

  // Check if we have searched at least once
  const hasSearched = Boolean(searchBounds)
  const isSearching = hasSearched && isLoading
  //const isIdleOrLoading = isIdle || isLoading

  const markers: ExtendedMarkerOptions[] = useMemo(() => {
    return (
      data?.map((asset) => {
        const [lng, lat] = asset.location.coordinates
        return {
          color,
          id: asset.id,
          position: { lat, lng },
          title: asset.data[titleField as string] ?? '-',
        }
      }) ?? []
    )
  }, [color, data, titleField])

  // selected asset
  const [selectedAsset, setSelectedAsset] = useState<string>()

  const handleSelectAsset = useCallback((assetId: string) => {
    setSelectedAsset(assetId)
    setSelectedTab(Tabs.List)

    const timeout = setTimeout(() => {
      setSelectedAsset(undefined)
    }, 3000)

    return () => clearTimeout(timeout)
  }, [])

  // Layout calculations for loading overlay
  const isIOS = isPlatform('ios')
  const tabsHeight = isIOS ? 24 : 48
  const controlsHeight = selectedTab === Tabs.Map ? 48 : 0
  const totalHeaderHeight = tabsHeight + controlsHeight
  const overlayStyle = { top: totalHeaderHeight }

  // Show loading overlay only when actively searching
  //const showLoadingOverlay = isSearching

  // Show search button when:
  // - On map tab
  // - Auto search is OFF
  // - Either hasn't searched yet OR map has moved (bounds changed)
  const showSearchButton = selectedTab === Tabs.Map && !autoSearch

  return (
    <AppLayout
      pageTitle={t('import.type', {
        label: modelTitle,
      })}
    >
      <div className="import-asset-by-location-content">
        <IonSegment value={selectedTab} onIonChange={handleTabChange}>
          <IonSegmentButton layout="icon-start" value={Tabs.Map}>
            <IonIcon icon={mapOutline} />
            <IonLabel>{t('import.advancedSearch.location.map')}</IonLabel>
          </IonSegmentButton>
          <IonSegmentButton
            layout="icon-start"
            value={Tabs.List}
            disabled={isLoading}
            /*  disabled={isIdle || isLoading} */
          >
            <IonIcon icon={listOutline} />
            <IonLabel>{t('import.advancedSearch.location.list')}</IonLabel>
          </IonSegmentButton>
        </IonSegment>

        {selectedTab === Tabs.Map && (
          <IonItem lines="full" className="search-controls">
            <IonLabel>
              {t('import.advancedSearch.location.autoSearch')}
            </IonLabel>
            <IonToggle
              slot="end"
              checked={autoSearch}
              onIonChange={(e) => handleAutoSearchToggle(e.detail.checked)}
            />
          </IonItem>
        )}

        {isSearching && (
          <LoadingOverlay text={t('import.searching')} style={overlayStyle} />
        )}

        <div
          className="import-by-location-view"
          data-selected={selectedTab === Tabs.Map}
        >
          <GoogleMaps
            markers={markers}
            onBoundsUpdated={handleBoundsUpdated}
            onInfoWindowClick={handleSelectAsset}
          />

          {showSearchButton && !isLoading && (
            <div className="search-button-container">
              <IonButton
                onClick={handleManualSearch}
                disabled={isSearching}
                className="search-button"
              >
                <IonIcon icon={searchOutline} slot="start" />
                {t('import.advancedSearch.location.search')}
              </IonButton>
            </div>
          )}
        </div>

        <div
          className="import-by-location-view"
          data-selected={selectedTab === Tabs.List}
        >
          <AssetCardList
            modelId={modelId}
            assets={assets}
            selectedAsset={selectedAsset}
          />
        </div>
      </div>
    </AppLayout>
  )
}

export default ImportAssetByLocation
